import type { components } from '@/api/types.gen';

import { hasPermission, Permission } from './permissions';
import type { User } from './user';

/** checkbox answers are a list of ticked options; older ones are comma-joined strings */
export type QuestionnaireAnswer = string | string[];

export const DEFAULT_EVENT_DURATION_MS = 2 * 60 * 60 * 1000;

export const EventType = {
  Community: 'community',
  Official: 'official',
  Club: 'club',
} as const;

export type EventTypeValue = (typeof EventType)[keyof typeof EventType];

export function isOfficialOrClub(type: string): boolean {
  return type === EventType.Official || type === EventType.Club;
}

export const EventVisibility = {
  Public: 'public',
  MembersOnly: 'members_only',
  InviteOnly: 'invite_only',
} as const;

export const EventStatus = {
  Active: 'active',
  Cancelled: 'cancelled',
  Draft: 'draft',
  Deleted: 'deleted',
} as const;

export const InvitePermission = {
  AllMembers: 'all_members',
  CoHostsOnly: 'co_hosts_only',
} as const;

export const RsvpStatus = {
  Attending: 'attending',
  Maybe: 'maybe',
  CantGo: 'cant_go',
} as const;

export type RsvpInputStatus = (typeof RsvpStatus)[keyof typeof RsvpStatus];

export const RSVP_STATUS_LABELS: { status: RsvpInputStatus; label: string }[] = [
  { status: RsvpStatus.Attending, label: "i'm going" },
  { status: RsvpStatus.Maybe, label: 'maybe' },
  { status: RsvpStatus.CantGo, label: "can't go" },
];

export function isRsvpInputStatus(status: string | null): status is RsvpInputStatus {
  return RSVP_STATUS_LABELS.some((s) => s.status === status);
}

export const RsvpServerStatus = {
  ...RsvpStatus,
  Waitlisted: 'waitlisted',
} as const;

export type RsvpServerStatusValue = (typeof RsvpServerStatus)[keyof typeof RsvpServerStatus];

export const RSVP_GROUP_LABELS: { status: RsvpServerStatusValue; label: string }[] = [
  { status: RsvpServerStatus.Attending, label: 'going' },
  { status: RsvpServerStatus.Maybe, label: 'maybe' },
  { status: RsvpServerStatus.CantGo, label: "can't go" },
  { status: RsvpServerStatus.Waitlisted, label: 'waitlisted' },
];

export function rsvpGroupLabel(status: string): string {
  return RSVP_GROUP_LABELS.find((s) => s.status === status)?.label ?? status;
}

export const AttendanceStatus = {
  Unknown: 'unknown',
  Attended: 'attended',
  DidntGo: 'didnt_go',
} as const;
export type AttendanceStatusValue = (typeof AttendanceStatus)[keyof typeof AttendanceStatus];

// A curated, admin-managed event tag (e.g. "walk", "restaurant meetup").
export interface EventTag {
  id: string;
  name: string;
  slug: string;
}

export type EventRsvpQuestionType = components['schemas']['EventRsvpQuestionIn']['field_type'];

export interface EventRsvpQuestion {
  id: string;
  label: string;
  fieldType: EventRsvpQuestionType;
  options: string[];
  required: boolean;
}

export interface EventGuest {
  userId: string;
  name: string;
  status: string;
  phone: string | null;
  photoUrl: string;
  hasPlusOne: boolean;
  attendance: AttendanceStatusValue;
  plusOneAttendance: AttendanceStatusValue;
  isMember: boolean;
  paidConfirmed: boolean;
  paymentVerified: boolean;
  /** Host-only RSVP question snapshots; empty for non-hosts. */
  questionnaireResponses: Record<string, { label: string; answer: QuestionnaireAnswer }>;
}

export interface EventCancellation {
  userId: string;
  name: string;
  cancelledAt: Date;
  daysBeforeEvent: number;
  within24h: boolean;
  afterStart: boolean;
  preCancelStatus: components['schemas']['RSVPStatus'] | null;
  declinedOutright: boolean;
}

export interface EventStats {
  goingCount: number;
  maybeCount: number;
  cantGoCount: number;
  noResponseCount: number;
  waitlistedCount: number;
  attendedCount: number;
  didntGoCount: number;
  notMarkedCount: number;
  cancellations: EventCancellation[];
}

/** Slug when the event has one, else the uuid — both resolve on the backend. */
export function eventPath(event: Pick<Event, 'id' | 'slug'>): string {
  return `/events/${event.slug || event.id}`;
}

export interface Event {
  id: string;
  slug: string;
  title: string;
  description: string;
  startDatetime: Date | null;
  endDatetime: Date | null;

  location: string;
  latitude: number | null;
  longitude: number | null;

  whatsappLink: string;
  partifulLink: string;
  otherLink: string;
  venmoLink: string;
  cashappLink: string;
  zelleInfo: string;
  price: string;

  rsvpEnabled: boolean;
  allowPlusOnes: boolean;
  maxAttendees: number | null;
  attendingCount: number;
  waitlistedCount: number;
  invitedCount: number;
  /** RSVP rows from outside the current co-hosts.
   *  0 means the API allows unpublishing back to draft. */
  guestRsvpCount: number;

  datetimeTbd: boolean;
  hasPoll: boolean;

  createdById: string | null;
  createdByName: string | null;
  createdByPhotoUrl: string;
  coHostIds: string[];
  coHostNames: string[];
  coHostPhotoUrls: string[];

  guests: EventGuest[];
  myRsvp: string | null;
  myPaidConfirmed: boolean;
  /** Snapshot responses for the viewer's RSVP: questionId → { label, answer }. */
  myQuestionnaireResponses: Record<string, { label: string; answer: QuestionnaireAnswer }>;
  rsvpQuestions: EventRsvpQuestion[];
  viewerUserId: string | null;
  invitedUserIds: string[];
  invitedUserNames: string[];
  invitedUserPhotoUrls: string[];
  invitePermission: string;

  pendingCohostInvites: PendingCohostInvite[];
  myPendingCohostInviteId: string | null;

  eventType: string;
  visibility: string;
  photoUrl: string;
  photoUpdatedAt: string | null;

  // Curated tags assigned to the event. Present on both list and detail.
  tags: EventTag[];

  isPast: boolean;
  status: string;
  isPartifulImport: boolean;
  isLegacy: boolean;
}

export interface PendingCohostInvite {
  id: string;
  userId: string;
  userName: string;
  userPhotoUrl: string;
  invitedAt: Date;
}

export function isHosting(event: Event, userId: string): boolean {
  return event.coHostIds.includes(userId);
}

export function eventHostCount(event: Event): number {
  return event.coHostIds.length + event.pendingCohostInvites.length;
}

export function canManageEvent(event: Event, user: User | null): boolean {
  if (!user) return false;
  if (isHosting(event, user.id)) return true;
  return hasPermission(user, Permission.ManageEvents);
}

// Mirrors backend TENTATIVE_MEMBER_EVENT_TYPES.
const TENTATIVE_MEMBER_RSVP_TYPES: readonly string[] = [EventType.Official, EventType.Club];

export function canTentativeMemberRsvp(event: Event): boolean {
  return TENTATIVE_MEMBER_RSVP_TYPES.includes(event.eventType);
}

// Mirrors backend is_tentative_member: only tentative applicants sign in without membership.
export function isTentativeMember(user: Pick<User, 'isMember'> | null | undefined): boolean {
  return user?.isMember === false;
}

export function canPublicRsvp(event: Event): boolean {
  return (
    event.eventType === EventType.Official &&
    event.visibility === EventVisibility.Public &&
    event.rsvpEnabled &&
    event.status !== EventStatus.Cancelled &&
    !event.isPast
  );
}

export function eventClass(e: Event): string {
  if (e.status === EventStatus.Cancelled) return 'pda-evt pda-evt-cancelled';
  if (e.eventType === EventType.Official) return 'pda-evt pda-evt-official';
  if (e.eventType === EventType.Club) return 'pda-evt pda-evt-club';
  if (e.visibility === EventVisibility.InviteOnly) return 'pda-evt pda-evt-invite';
  if (e.visibility === EventVisibility.MembersOnly) return 'pda-evt pda-evt-members';
  return 'pda-evt pda-evt-community';
}

export function spotsLeft(e: Event): number | null {
  if (e.maxAttendees === null) return null;
  return Math.max(0, e.maxAttendees - e.attendingCount);
}

export function myRsvpLabel(e: Event): string | null {
  switch (e.myRsvp) {
    case RsvpServerStatus.Attending:
      return 'going';
    case RsvpServerStatus.Maybe:
      return 'maybe';
    case RsvpServerStatus.CantGo:
      return "can't go";
    case RsvpServerStatus.Waitlisted:
      return 'waitlisted';
    default:
      return null;
  }
}
